import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  SmartToy as SmartToyIcon,
  FiberManualRecord as FiberManualRecordIcon,
  AccountBalanceWallet,
  RocketLaunch as CampaignIcon,
  Psychology as BotTrainingIcon,
  HourglassEmpty as QueueIcon,
  DoneAll as DoneAllIcon,
  Send as SendIcon,
  Summarize as TotalIcon,
  CallReceived as IncomingIcon,
  CallMade as OutgoingIcon,
  Reply as ReplyIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";

const toTitleCase = (str) =>
  (str || "")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");

const getGreeting = (userName = "") => {
  const hour = new Date().getHours();
  const base = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  return userName ? `${base}, ${toTitleCase(userName)} !` : `${base}!`;
};

const METRIC_ICONS = {
  Total: TotalIcon,
  "Total Msgs": TotalIcon,
  Queue: QueueIcon,
  Sent: SendIcon,
  Delivered: DoneAllIcon,
  Read: DoneAllIcon,
  Incoming: IncomingIcon,
  "Outgoing/Total": OutgoingIcon,
  Replies: ReplyIcon,
};

// Service config: display, routes, and metric-to-page mapping
const SERVICE_CONFIG = {
  personal_whatsapp: {
    name: "Personal WhatsApp",
    creditsKey: "pwc",
    icon: <WhatsAppIcon sx={{ fontSize: 28 }} />,
    color: "#25d366",
    schedulePath: "/personal-whatsapp",
    scheduleLabel: "Start Campaign",
    scheduleIcon: "campaign",
    metrics: [
      { key: "total_messages_count", label: "Total", path: "/message-queue", row: 1 },
      { key: "pending_count", label: "Queue", path: "/message-queue", row: 1, percentOf: "total_messages_count" },
      { key: "completed_count", label: "Sent", path: "/personal/whatsapp/report", row: 2, valueKeys: ["completed_count", "delivered_count", "read_count", "sent_count"], percentOf: "total_messages_count" },
      { key: "delivered_count", label: "Delivered", path: "/message-queue", row: 2, valueKeys: ["delivered_count", "read_count"], percentOf: "sent" },
      { key: "read_count", label: "Read", path: "/personal/whatsapp/report", row: 2, percentOf: "total_messages_count" },
    ],
  },
  email: {
    name: "Business Email",
    creditsKey: "bec",
    icon: <EmailIcon sx={{ fontSize: 28 }} />,
    color: "#1976d2",
    schedulePath: "/email-schedule",
    scheduleLabel: "Start Campaign",
    scheduleIcon: "campaign",
    metrics: [
      { key: "total_messages_count", label: "Total", path: "/email-queue", row: 1 },
      { key: "pending_count", label: "Queue", path: "/email-queue", row: 1, percentOf: "total_messages_count" },
      { key: "completed_count", label: "Sent", path: "/email-analytics", row: 2, percentOf: "total_messages_count" },
      { key: "read_count", label: "Read", path: "/email-analytics", row: 2, percentOf: "total_messages_count" },
    ],
  },
  whatsapp_business: {
    name: "WhatsApp Business",
    creditsKey: "bwc",
    icon: <WhatsAppIcon sx={{ fontSize: 28 }} />,
    color: "#128c7e",
    schedulePath: "/whatsapp-business/campaign",
    scheduleLabel: "Start Campaign",
    scheduleIcon: "campaign",
    metrics: [
      { key: "total_incoming_messages_count", label: "Incoming", path: "/whatsapp-business/chat-window", row: 1 },
      { key: "total_outgoing_messages_count", label: "Outgoing/Total", path: "/whatsapp-business/campaign-management", row: 1 },
      { key: "pending_count", label: "Queue", path: "/whatsapp-business/message-queue", row: 1, percentOf: "total_outgoing_messages_count" },
      { key: "sent_count", label: "Sent", path: "/whatsapp-business/message-queue", row: 2, valueKeys: ["sent_count", "delivered_count", "read_count", "completed_count"], percentOf: "total_outgoing_messages_count" },
      { key: "delivered_count", label: "Delivered", path: "/whatsapp-business/message-queue", row: 2, valueKeys: ["delivered_count", "read_count"], percentOf: "sent" },
      { key: "read_count", label: "Read", path: "/whatsapp-business/message-queue", row: 2, percentOf: "total_outgoing_messages_count" },
    ],
  },
  bot: {
    name: "WhatsApp Business Bot",
    creditsKey: null,
    icon: <SmartToyIcon sx={{ fontSize: 28 }} />,
    color: "#7c3aed",
    schedulePath: "/whatsapp-business/bot-training",
    scheduleLabel: "AI Agent",
    scheduleIcon: "bot",
    metrics: [
      { key: "total_messages_count", label: "Total Msgs", path: "/whatsapp-business/bot-training", row: 1 },
      { key: "total_reply_count", label: "Replies", path: "/whatsapp-business/bot-training", row: 1 },
      { key: "pending_reply_count", label: "Queue", path: "/whatsapp-business/message-queue", row: 1, percentOf: "total_reply_count" },
      { key: "sent_reply_count", label: "Sent", path: "/whatsapp-business/message-queue", row: 2, valueKeys: ["sent_reply_count", "delivered_reply_count", "read_reply_count", "completed_reply_count"], percentOf: "total_reply_count" },
      { key: "delivered_reply_count", label: "Delivered", path: "/whatsapp-business/message-queue", row: 2, valueKeys: ["delivered_reply_count", "read_reply_count"], percentOf: "sent" },
      { key: "read_reply_count", label: "Read", path: "/whatsapp-business/message-queue", row: 2, percentOf: "total_reply_count" },
    ],
  },
};

const CARD_SX = {
  elevation: 0,
  height: "100%",
  background: "rgba(255, 255, 255, 0.9)",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
  transition: "all 0.2s ease",
  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)" },
};

const CommandCenterDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiData, setApiData] = useState([]);
  const [userCredits, setUserCredits] = useState({});
  const [userName, setUserName] = useState("");
  const notify = useNotification();

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError("");
      try {
        let userId = Cookies.get("userId");
        try {
          userId = typeof userId === "string" ? JSON.parse(userId) : userId;
        } catch {
          userId = userId;
        }
        const [dashboardRes, creditsRes, userRoleRes, usersRes] = await Promise.all([
          axiosInstance.get("/dashboard"),
          axiosInstance.get("/user-credits").catch(() => ({ data: { success: false } })),
          userId ? axiosInstance.get(`/whatsapp/personal/get/user_role/v1/${userId}`).catch(() => ({ data: { data: [{}] } })) : Promise.resolve({ data: { data: [{}] } }),
          axiosInstance.get("/whatsapp-business/users/all").catch(() => ({ data: [] })),
        ]);
        const userRoleData = userRoleRes?.data?.data?.[0];
        let name = userRoleData?.name || userRoleData?.user?.name || "";
        if (!name && usersRes?.data) {
          const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || [];
          const match = users.find((u) => String(u.id) === String(userId) || u.user_id === userId);
          name = match?.name || "";
        }
        if (name) setUserName(name);
        if (dashboardRes.data?.success) {
          const data = dashboardRes.data.data;
          const services = Array.isArray(data) ? data : (data?.services ?? []);
          setApiData(services);
        } else {
          setApiData([]);
        }
        if (creditsRes?.data?.success && creditsRes.data?.data) {
          setUserCredits(creditsRes.data.data);
        } else {
          setUserCredits({});
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Failed to fetch dashboard data";
        setError(msg);
        setApiData([]);
        notify(msg, "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        <Typography variant="body2" color="text.secondary">Loading dashboard…</Typography>
      </Box>
    );
  }

  if (error && apiData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "#F7F9FC", p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.02em" }}>
          {getGreeting(userName)}
        </Typography>
      </Box>

      {/* Service cards – each service with metrics linked to relevant pages */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {apiData.map((service) => {
          const config = SERVICE_CONFIG[service.service_name];
          if (!config) return null;
          return (
            <Grid item xs={12} sm={6} lg={6} key={service.service_name}>
              <Card
                elevation={0}
                sx={{
                  ...CARD_SX,
                  minHeight: 220,
                  background: "rgba(255, 255, 255, 0.95)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  "&:hover": { boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)" },
                }}
              >
                <CardContent sx={{ p: 3.5, "&:last-child": { pb: 3.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                    <Box sx={{ bgcolor: `${config.color}20`, p: 1.25, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: config.color }}>
                      {config.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {config.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 10, color: "success.main" }} />
                        <Typography variant="caption" color="text.secondary">Active</Typography>
                        {config.creditsKey != null && (
                          <Box
                            component="span"
                            onClick={() => navigate("/credits-info")}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.6,
                              ml: 1.5,
                              px: 1.25,
                              py: 0.4,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: `${config.color}40`,
                              bgcolor: `${config.color}12`,
                              cursor: "pointer",
                              transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                borderColor: config.color,
                                bgcolor: `${config.color}20`,
                                boxShadow: `0 2px 8px ${config.color}35`,
                                transform: "translateY(-1px)",
                                "& .credits-icon": { color: config.color },
                              },
                            }}
                          >
                            <AccountBalanceWallet className="credits-icon" sx={{ fontSize: 15, color: config.color, opacity: 0.9, transition: "opacity 0.2s ease" }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.8rem" }}>
                              {(userCredits[config.creditsKey] ?? 0).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {config.schedulePath && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={config.scheduleIcon === "bot" ? <BotTrainingIcon /> : <CampaignIcon />}
                        onClick={() => navigate(config.schedulePath)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          bgcolor: config.color,
                          "&:hover": { bgcolor: config.color, filter: "brightness(0.9)" },
                        }}
                      >
                        {config.scheduleLabel}
                      </Button>
                    )}
                  </Box>
                  <Box>
                    {[1, 2].map((rowNum) => (
                      <Box key={rowNum} sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: rowNum < 2 ? 1.25 : 0 }}>
                        {(() => {
                          const sentMetric = config.metrics.find((x) => x.label === "Sent");
                          const sentValue = sentMetric
                            ? (sentMetric.valueKeys
                                ? sentMetric.valueKeys.reduce((s, k) => s + (Number(service[k]) || 0), 0)
                                : (Number(service[sentMetric.key]) || 0))
                            : 0;
                          return config.metrics
                            .filter((m) => (m.row ?? 1) === rowNum)
                            .map((m) => {
                              const value = m.valueKeys
                                ? (m.valueKeys.reduce((sum, k) => sum + (Number(service[k]) || 0), 0))
                                : (service[m.key] ?? 0);
                              const displayValue = m.format
                                ? m.format(service)
                                : typeof value === "number"
                                ? value.toLocaleString()
                                : value;
                              const denominator = m.percentOf === "sent" ? sentValue : (m.percentOf ? (Number(service[m.percentOf]) || 0) : 0);
                              const percent = denominator > 0 && typeof value === "number" ? Math.round((value / denominator) * 10000) / 100 : null;
                              const isQueue = m.label === "Queue";
                              const isRead = m.label === "Read";
                              const MetricIcon = METRIC_ICONS[m.label] || SendIcon;
                              return (
                              <Box
                                key={m.key}
                                onClick={() => navigate(m.path)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.5,
                                  py: 0.75,
                                  borderRadius: 2,
                                  border: "1.5px solid",
                                  borderColor: isQueue ? "rgba(234,179,8,0.5)" : "rgba(0,0,0,0.12)",
                                  bgcolor: isQueue ? "rgba(234,179,8,0.12)" : "rgba(255,255,255,0.8)",
                                  cursor: "pointer",
                                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                  "&:hover": {
                                    borderColor: isQueue ? "#eab308" : config.color,
                                    borderWidth: "1.5px",
                                    bgcolor: isQueue ? "rgba(234,179,8,0.2)" : `${config.color}12`,
                                    boxShadow: isQueue ? "0 4px 14px rgba(234,179,8,0.3)" : `0 4px 14px ${config.color}40`,
                                    transform: "translateY(-2px)",
                                    "& .metric-value": { color: isQueue ? "#a16207" : config.color },
                                    "& .metric-icon": { opacity: 1, color: isQueue ? "#a16207" : isRead ? "#0d47a1" : config.color },
                                  },
                                  "&:active": { transform: "translateY(0)" },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isQueue ? "#a16207" : "text.secondary",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.6px",
                                  }}
                                >
                                  {m.label}
                                </Typography>
                                <Typography
                                  className="metric-value"
                                  component="span"
                                  sx={{
                                    fontWeight: 700,
                                    color: isQueue ? "#a16207" : "text.primary",
                                    fontSize: "0.85rem",
                                    transition: "all 0.25s ease",
                                  }}
                                >
                                  {displayValue}
                                  {percent != null && (
                                    <Typography component="span" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", ml: 0.5 }}>
                                      ({percent}%)
                                    </Typography>
                                  )}
                                </Typography>
                                <MetricIcon
                                  className="metric-icon"
                                  sx={{
                                    fontSize: 14,
                                    opacity: 0.4,
                                    color: isRead ? "#0d47a1" : "inherit",
                                    transition: "all 0.25s ease",
                                  }}
                                />
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CommandCenterDashboard;
